/**
 * Global Product Card Hydration
 *
 * Finds placeholder cards (rendered by product-card-global.liquid snippet) and replaces them
 * with fully rendered cards from the global-product-card section via the Section Rendering API.
 *
 * Each placeholder has data-product-url which is used to fetch the global card section
 * rendered in the context of that product.
 */

import { ThemeEvents } from '@theme/events';

/** @type {string} */
const GLOBAL_SECTION_TYPE = 'global-product-card';

/** @type {string | null} */
let globalSectionId = null;

/**
 * Gets the section ID from the global product card section element.
 * Falls back to the type name if the element is not found.
 * @returns {string}
 */
function getSectionId() {
  if (globalSectionId) return globalSectionId;

  const sectionEl = document.querySelector('[data-global-product-card][data-section-id]');
  if (sectionEl) {
    globalSectionId = sectionEl.getAttribute('data-section-id');
  }

  return globalSectionId || GLOBAL_SECTION_TYPE;
}

/** @type {Map<string, Promise<string>>} */
const fetchCache = new Map();

function clearGlobalCardCache() {
  fetchCache.clear();
}

function resetHydratedPlaceholders(root = document) {
  const placeholders = root.querySelectorAll('[data-global-card-placeholder][data-hydrated]');

  for (const placeholder of placeholders) {
    placeholder.removeAttribute('data-hydrated');
    placeholder.classList.remove('global-product-card--hydrated');
  }
}

/**
 * Returns all global card placeholders inside a root, including the root itself when applicable.
 * @param {ParentNode | Element} root
 * @returns {Element[]}
 */
function getPlaceholders(root) {
  /** @type {Element[]} */
  const placeholders = [];

  if (root instanceof Element && root.hasAttribute('data-global-card-placeholder')) {
    placeholders.push(root);
  }

  if ('querySelectorAll' in root) {
    placeholders.push(...root.querySelectorAll('[data-global-card-placeholder]:not([data-placeholder="true"]):not([data-hydrated])'));
  }

  return placeholders;
}

/**
 * Fetches the global product card section HTML for a given product URL.
 * Caches results to avoid duplicate fetches for the same product.
 *
 * @param {string} productUrl - The product URL (e.g., /products/some-handle)
 * @returns {Promise<string>} The card inner HTML
 */
async function fetchGlobalCard(productUrl) {
  const cacheKey = productUrl;

  if (fetchCache.has(cacheKey)) {
    return /** @type {Promise<string>} */ (fetchCache.get(cacheKey));
  }

  const sectionId = getSectionId();

  const promise = (async () => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Failed to fetch global card for ${productUrl}: ${response.status}`);
    }

    const html = await response.text();

    // Parse the HTML and extract the card content from #global-product-card-output
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const output = doc.querySelector('#global-product-card-output');

    if (!output) {
      throw new Error(`Could not find #global-product-card-output in response for ${productUrl}`);
    }

    return output.innerHTML;
  })();

  fetchCache.set(cacheKey, promise);

  return promise;
}

/**
 * Hydrates all global product card placeholders inside a root.
 * Groups by product URL to batch requests efficiently.
 * @param {Document | Element | ParentNode} [root=document]
 * @returns {Promise<void>}
 */
export async function hydrateGlobalCardsInRoot(root = document) {
  const placeholders = getPlaceholders(root);

  if (placeholders.length === 0) return;

  // Group placeholders by product URL
  /** @type {Map<string, Element[]>} */
  const byUrl = new Map();

  for (const placeholder of placeholders) {
    const productUrl = placeholder.getAttribute('data-product-url');

    if (!productUrl) continue;

    if (!byUrl.has(productUrl)) {
      byUrl.set(productUrl, []);
    }

    /** @type {Element[]} */ (byUrl.get(productUrl)).push(placeholder);
  }

  // Fetch all unique product cards (limited concurrency)
  const urls = [...byUrl.keys()];
  const batchSize = 4;

  for (let i = 0; i < urls.length; i += batchSize) {
    const batch = urls.slice(i, i + batchSize);

    const results = await Promise.allSettled(
      batch.map(async (productUrl) => {
        const cardHTML = await fetchGlobalCard(productUrl);
        const targets = /** @type {Element[]} */ (byUrl.get(productUrl));

        for (const placeholder of targets) {
          placeholder.innerHTML = cardHTML;
          placeholder.setAttribute('data-hydrated', 'true');
          placeholder.classList.add('global-product-card--hydrated');
        }
      })
    );

    // Log any failures for debugging
    for (const result of results) {
      if (result.status === 'rejected') {
        console.warn('[Global Product Card]', result.reason);
      }
    }
  }
}

async function hydrateGlobalCards() {
  await hydrateGlobalCardsInRoot(document);
}

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateGlobalCards);
} else {
  hydrateGlobalCards();
}

// Re-run when Shopify updates sections (e.g., in the theme editor)
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;

    if (section instanceof Element && section.matches('[data-global-product-card], .global-product-card-section')) {
      globalSectionId = section.getAttribute('data-section-id') || globalSectionId;
      clearGlobalCardCache();
      resetHydratedPlaceholders(document);
    }

    void hydrateGlobalCards();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target;

    if (!(section instanceof Element) || !section.matches('[data-global-product-card], .global-product-card-section')) return;

    clearGlobalCardCache();
    resetHydratedPlaceholders(document);
    void hydrateGlobalCards();
  });
}

// Observe for dynamically added placeholders (e.g., infinite scroll, AJAX pagination)
// Also observe attribute changes to detect when morph removes `data-hydrated` after a filter update
const observer = new MutationObserver((mutations) => {
  let needsHydration = false;

  for (const mutation of mutations) {
    // New placeholder nodes added (e.g. infinite scroll, pagination)
    if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element && (
          node.hasAttribute('data-global-card-placeholder') ||
          node.querySelector('[data-global-card-placeholder]')
        )) {
          needsHydration = true;
          break;
        }
      }
    }

    // Attribute change: morph removes `data-hydrated` from existing placeholders after filter re-render
    if (mutation.type === 'attributes' && mutation.attributeName === 'data-hydrated') {
      const target = mutation.target;
      if (
        target instanceof Element &&
        target.hasAttribute('data-global-card-placeholder') &&
        !target.hasAttribute('data-hydrated') &&
        !target.hasAttribute('data-placeholder')
      ) {
        needsHydration = true;
      }
    }

    if (needsHydration) break;
  }

  if (needsHydration) hydrateGlobalCards();
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-hydrated'],
  attributeOldValue: false,
});

/**
 * After a filter update the section is re-rendered via morph which reverts
 * hydrated placeholders to their server-rendered fallback (removing data-hydrated).
 * The attribute observer above will catch this and trigger re-hydration.
 *
 * Additionally, we immediately strip `data-hydrated` ourselves BEFORE morph runs
 * so the CSS `visibility:hidden` rule activates at once — the user never sees
 * the basic fallback flash even for a single frame.
 */
document.addEventListener(ThemeEvents.FilterUpdate, () => {
  clearGlobalCardCache();

  // Immediately hide all currently-hydrated placeholders so the CSS
  // visibility:hidden rule kicks in before morph reverts their content.
  resetHydratedPlaceholders(document);

  // Belt-and-suspenders: re-hydrate after morph has settled.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hydrateGlobalCards();
    });
  });
});
